import {
  ArcElement,
  type Chart,
  Chart as ChartJS,
  type ChartTypeRegistry,
  Legend,
  type LegendItem,
  Title,
  Tooltip,
  type TooltipOptions,
} from 'chart.js';
import { forwardRef, useId, useMemo } from 'react';
import { Pie } from 'react-chartjs-2';
import { twMerge } from 'tailwind-merge';
import { PieChartLabelsPlugin } from '@/utils/PieChartLabelsPlugin.ts';

import { accountingFormatNumber, getLastSevenDays, randomDataSetsTDMTotal } from '@/utils';

ChartJS.register(ArcElement, Title, Tooltip, Legend, PieChartLabelsPlugin);

type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

type PieChartProps = {
  labels: string[];
  datasets: { data: any; color: string; formattedData?: string }[];
  chartTitle?: string;
  legendPosition?: any;
  legendAlign?: any;
  showValue?: boolean;
  showValueAndPercentage?: boolean;
  showSlicePercentage?: boolean;
  currency?: boolean;
  width?: number;
  maintainAspectRatio?: boolean;
  displayLegend?: boolean;
  tooltipPlugin?: DeepPartial<TooltipOptions<keyof ChartTypeRegistry>>;
  abbreviate?: boolean;
  containerClassName?: string;
};

const PieChart = forwardRef<Chart<'pie'> | undefined, PieChartProps>(
  (
    {
      labels,
      datasets,
      chartTitle = '',
      legendPosition,
      legendAlign,
      showValue = false,
      currency = false,
      showValueAndPercentage,
      showSlicePercentage = false,
      width,
      maintainAspectRatio = false,
      displayLegend,
      tooltipPlugin,
      abbreviate,
      containerClassName,
    },
    ref
  ) => {
    const chartId = useId();
    const lastSevendaysFormatted: string[] = [];
    getLastSevenDays().forEach((date) => {
      lastSevendaysFormatted.push(date.toDateString());
    });

    const totalProject = randomDataSetsTDMTotal(datasets);
    const userData: any = [];
    const dataColors: string[] = [];
    datasets.forEach((d) => {
      userData.push(d.data);
      dataColors.push(d.color);
    });

    const getPercentageLabel = (value: number | null | undefined) => {
      if (!totalProject || value == null) return '0%';
      return `${((Number(value) / totalProject) * 100).toFixed(1)}%`;
    };

    const chartKey = useMemo(() => JSON.stringify({ labels, datasets }), [labels, datasets]);

    return (
      <div className={showSlicePercentage ? twMerge('relative h-[260px] w-full overflow-visible', containerClassName) : containerClassName}>
        <Pie
          key={chartKey}
          ref={ref}
          id={chartId}
          width={width}
          className={showSlicePercentage ? 'max-h-full max-w-full' : undefined}
          data={{
            labels: labels,
            datasets: [
              {
                label: chartTitle,
                data: userData,
                backgroundColor: dataColors,
              },
            ],
          }}
          options={{
            maintainAspectRatio,
            layout: showSlicePercentage ? { padding: { top: 44, right: 52, bottom: 24, left: 64 } } : undefined,
            plugins: {
              pieLabelPlugin: {
                display: showSlicePercentage,
                minSlicePercent: 0,
              },
              tooltip: tooltipPlugin as any,
              legend: {
                display: displayLegend,
                position: legendPosition ? legendPosition : 'left',
                align: legendAlign ? legendAlign : 'end',
                labels: {
                  usePointStyle: true,
                  pointStyle: 'circle',
                  font: { size: 14 },
                  padding: 10,
                  generateLabels: (chart: Chart<keyof ChartTypeRegistry, (number | null)[], unknown>): LegendItem[] => {
                    const { data } = chart;

                    //if (data?.labels?.length && showValueAndPercentage) {
                    //  return data.labels.map((label, i) => ({
                    //    text: `${label}: ${datasets[i]?.formattedData ?? getPercentageLabel(data?.datasets[0]?.data[i] as number)}`,
                    //    fillStyle: dataColors[i],
                    //    strokeStyle: dataColors[i],
                    //    lineWidth: 2,
                    //    datasetIndex: 0,
                    //  }));
                    //}

                    if (data?.labels?.length && showValue) {
                      return data.labels.map((label, i) => ({
                        text: `${label}: ${data?.datasets[0]?.data[i]}%`,
                        fillStyle: dataColors[i],
                        strokeStyle: dataColors[i],
                        lineWidth: 2,
                        datasetIndex: 0,
                      }));
                    }
                    if (data?.labels?.length && currency) {
                      return data.labels.map((label, i) => ({
                        text: `${label}: ${accountingFormatNumber({
                          num: data?.datasets[0]?.data[i] ?? 0,
                          format: 'en-US',
                          currency: 'USD',
                          abbreviate,
                        })}`,
                        fillStyle: dataColors[i],
                        strokeStyle: dataColors[i],
                        lineWidth: 2,
                        datasetIndex: 0,
                      }));
                    }
                    if (data?.labels?.length) {
                      return data.labels.map((label, i) => ({
                        text: `${label}: ${data?.datasets[0]?.data[i]}`,
                        fillStyle: dataColors[i],
                        strokeStyle: dataColors[i],
                        lineWidth: 2,
                        datasetIndex: 0,
                      }));
                    }
                    return [];
                  },
                },
              },
            },
            scales: {
              x: { display: false },
              y: { display: false },
            },
          }}
        />
      </div>
    );
  }
);

export default PieChart;
